import deepMerge from "./deepMerge";
import queryString from "query-string";
import { CLIENT_CONFIG_DEFAULTS } from "../constants/clientConfigDefaults";
import { CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS } from "../constants/clientConfigDefaultWidgetOptions";
import { PORTAL_IDS } from "../constants/portals";
import { portalConfig } from "./portal";
import { DEFAULT_WIDGET, WIDGETS } from "../constants/widgets";
import { LIMIT_FIELDS } from "../constants/limitFields";
import { OPTION_FIELDS } from "../constants/optionFields";
import { FILTER_FIELDS } from "../constants/filterFields";
import { WIDGET_OPTION_FIELDS } from "../constants/widgetOptionFields";
import { convertValues } from "./convertValues";
import { CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS as beClientConfigDefaultWidgetOptions } from "../constants/portals/beTelecom/clientConfigDefaultWidgetOptions";
import { CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS as chClientConfigDefaultWidgetOptions } from "../constants/portals/chTelecom/clientConfigDefaultWidgetOptions";
import { CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS as deClientConfigDefaultWidgetOptions } from "../constants/portals/deTelecom/clientConfigDefaultWidgetOptions";
import { CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS as esClientConfigDefaultWidgetOptions } from "../constants/portals/esTelecom/clientConfigDefaultWidgetOptions";
import { CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS as frClientConfigDefaultWidgetOptions } from "../constants/portals/frTelecom/clientConfigDefaultWidgetOptions";
import { CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS as itClientConfigDefaultWidgetOptions } from "../constants/portals/itTelecom/clientConfigDefaultWidgetOptions";
import { CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS as nlClientConfigDefaultWidgetOptions } from "../constants/portals/nlTelecom/clientConfigDefaultWidgetOptions";
import { CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS as plClientConfigDefaultWidgetOptions } from "../constants/portals/plTelecom/clientConfigDefaultWidgetOptions";
import { CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS as ukClientConfigDefaultWidgetOptions } from "../constants/portals/ukTelecom/clientConfigDefaultWidgetOptions";
import { CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS as usClientConfigDefaultWidgetOptions } from "../constants/portals/usTelecom/clientConfigDefaultWidgetOptions";
import { mergeLimitsWithFilter } from "./limits";
import { getClientData } from "../api/client";
import { getPhoneVariantsByEans } from "../api/phoneVariant";
import { offerType } from "./offerType";
import {
  getPhoneCategoryByEan,
  getPhoneCategoryByPhoneId,
  getPhoneCategoryByPhoneVariantId
} from "../api/phoneCategory";
import { OFFER_TYPES } from "../constants/offerTypes";

function defaultConfig(clientConfig = {}) {
  let defaultClientConfig = CLIENT_CONFIG_DEFAULTS;
  defaultClientConfig.widget = DEFAULT_WIDGET;
  defaultClientConfig.widgetOptions = initialWidgetOptions(clientConfig);

  return defaultClientConfig;
}

function waitForDomElement(clientConfig) {
  const elementId = clientConfig.elementId;

  return new Promise(function waitForVar(resolve) {
    if (document.getElementById(elementId) != null) {
      return resolve();
    }
    setTimeout(() => waitForVar(resolve), 100);
  });
}

function waitForClientConfigs() {
  return new Promise(function waitForVar(resolve) {
    if (window.tdsPcInpageConfig !== undefined) {
      return resolve();
    }
    setTimeout(() => waitForVar(resolve), 100);
  });
}

function waitForCustomTheme(clientConfig, clientData) {
  const variableName = themeVariableName(clientConfig);

  if (variableName && clientConfig.options.themeIdentifier) {
    let clientStyle;
    for (const key of Object.keys(clientData.clientStyles)) {
      if (
        clientData.clientStyles[key].identifier ===
        clientConfig.options.themeIdentifier
      ) {
        clientStyle = clientData.clientStyles[key];
      }
    }

    if (clientStyle) {
      window[variableName] = clientStyle.jsTheme;
    } else {
      window[variableName] = {};
    }
  }

  return new Promise(function waitForVar(resolve) {
    if (!variableName || window[variableName] !== undefined) {
      return resolve();
    }
    setTimeout(() => waitForVar(resolve), 100);
  });
}

function waitForInitialPortalData(clientConfig) {
  let initialPortalData;
  const eans = clientConfig.limits.phoneVariantEan;

  const clientDataPromise = getClientData(clientConfig);
  const phoneVariantsByEansPromise = getPhoneVariantsByEans(clientConfig, eans);
  const phoneCategoryPromise = getPhoneCategory(clientConfig);

  Promise.all([
    clientDataPromise,
    phoneVariantsByEansPromise.catch((e) => {
      return [];
    }),
    phoneCategoryPromise.catch((e) => {
      return undefined;
    })
  ]).then((values) => {
    initialPortalData = {};
    initialPortalData.clientData = values[0];

    let phoneVariantIds = [];
    for (const phoneVariantValue of values[1]) {
      if (phoneVariantValue != null) {
        const phoneVariantId = phoneVariantValue.id;

        if (
          phoneVariantId != null &&
          phoneVariantIds.indexOf(phoneVariantId) < 0
        ) {
          phoneVariantIds.push(phoneVariantId);
        }
      }
    }

    initialPortalData.phoneVariantIds = phoneVariantIds;
    initialPortalData.phoneCategory = values[2];
  });

  return new Promise(function waitForVar(resolve) {
    if (initialPortalData != null) {
      return resolve(initialPortalData);
    }
    setTimeout(() => waitForVar(resolve), 100);
  });
}

function getPhoneCategory(clientConfig) {
  const currentOfferType = offerType(clientConfig);
  const initialFilter =
    clientConfig.widgetOptions[currentOfferType].initialFilter;
  const defaultInitialFilter = defaultConfig(clientConfig).widgetOptions[
    currentOfferType
  ].initialFilter;

  if (
    defaultInitialFilter.maxPrice !== undefined &&
    initialFilter.maxPrice === defaultInitialFilter.maxPrice
  ) {
    if (clientConfig.limits.phoneVariantEan.length === 1) {
      return getPhoneCategoryByEan(
        clientConfig,
        clientConfig.limits.phoneVariantEan[0]
      );
    } else if (clientConfig.limits.phoneVariantId.length === 1) {
      return getPhoneCategoryByPhoneVariantId(
        clientConfig,
        clientConfig.limits.phoneVariantId[0]
      );
    } else if (clientConfig.limits.phoneId.length === 1) {
      return getPhoneCategoryByPhoneId(
        clientConfig,
        clientConfig.limits.phoneId[0]
      );
    } else if (initialFilter.phoneVariantId.length === 1) {
      return getPhoneCategoryByPhoneVariantId(
        clientConfig,
        initialFilter.phoneVariantId[0]
      );
    } else if (initialFilter.phoneId.length === 1) {
      return getPhoneCategoryByPhoneId(clientConfig, initialFilter.phoneId[0]);
    } else {
      return Promise.resolve(undefined);
    }
  } else {
    return Promise.resolve(undefined);
  }
}

function urlQueryStringToConfig(urlQuery) {
  let config = {};

  const urlParams = queryString.parse(urlQuery, {
    arrayFormat: "bracket"
  });

  for (const key of Object.keys(urlParams)) {
    config = deepMerge(config, urlQueryStringPartToConfig(key, urlParams[key]));
  }

  return config;
}

function urlQueryStringPartToConfig(key, value) {
  if (key.indexOf("[") < 0) {
    return { [key]: value };
  } else {
    const subKey = key.substring(
      key.lastIndexOf("[") + 1,
      key.lastIndexOf("]")
    );
    const newKey = key.substring(0, key.lastIndexOf("["));
    return urlQueryStringPartToConfig(newKey, { [subKey]: value });
  }
}

function getPortalInitialWidgetOptions(portalId) {
  switch (portalId) {
    case PORTAL_IDS.beTelecom:
      return beClientConfigDefaultWidgetOptions;
    case PORTAL_IDS.chTelecom:
      return chClientConfigDefaultWidgetOptions;
    case PORTAL_IDS.deTelecom:
      return deClientConfigDefaultWidgetOptions;
    case PORTAL_IDS.esTelecom:
      return esClientConfigDefaultWidgetOptions;
    case PORTAL_IDS.frTelecom:
      return frClientConfigDefaultWidgetOptions;
    case PORTAL_IDS.itTelecom:
      return itClientConfigDefaultWidgetOptions;
    case PORTAL_IDS.nlTelecom:
      return nlClientConfigDefaultWidgetOptions;
    case PORTAL_IDS.plTelecom:
      return plClientConfigDefaultWidgetOptions;
    case PORTAL_IDS.ukTelecom:
      return ukClientConfigDefaultWidgetOptions;
    case PORTAL_IDS.usTelecom:
      return usClientConfigDefaultWidgetOptions;
    default:
      return nlClientConfigDefaultWidgetOptions;
  }
}

function initialWidgetOptions(clientConfig) {
  let widget;

  if (
    clientConfig == null ||
    clientConfig.widget == null ||
    !WIDGETS[clientConfig.widget]
  ) {
    widget = DEFAULT_WIDGET;
  } else {
    widget = clientConfig.widget;
  }

  let portalInitialWidgetOptions;
  if (clientConfig && clientConfig.portal) {
    portalInitialWidgetOptions = getPortalInitialWidgetOptions(
      clientConfig.portal
    );
  } else {
    portalInitialWidgetOptions = {};
  }

  let baseInitialWidgetOptions;
  if (portalInitialWidgetOptions.base != null) {
    baseInitialWidgetOptions = deepMerge(
      CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS.base,
      portalInitialWidgetOptions.base
    );
  } else {
    baseInitialWidgetOptions = deepMerge(
      CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS.base,
      {}
    );
  }

  let widgetInitialWidgetOptions;
  if (portalInitialWidgetOptions[WIDGETS[widget].id] != null) {
    widgetInitialWidgetOptions = deepMerge(
      CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS[WIDGETS[widget].id],
      portalInitialWidgetOptions[WIDGETS[widget].id]
    );
  } else {
    widgetInitialWidgetOptions = deepMerge(
      CLIENT_CONFIG_DEFAULT_WIDGET_OPTIONS[WIDGETS[widget].id],
      {}
    );
  }

  return deepMerge(baseInitialWidgetOptions, widgetInitialWidgetOptions);
}

function addFullComparatorUrl(clientConfig, clientData) {
  const clientDomains = clientData.clientDomains;
  const currentDomain = window.location.hostname;
  let fullComparatorUrl;

  if (clientDomains) {
    let clientDomain;
    for (const key of Object.keys(clientDomains)) {
      if (currentDomain.indexOf(clientDomains[key].name) >= 0) {
        clientDomain = clientDomains[key];
      }
    }

    if (clientDomain) {
      fullComparatorUrl = clientDomain.fullComparatorUrl;
    }
  }

  fullComparatorUrl =
    clientConfig.options.fullComparatorUrl || fullComparatorUrl;

  clientConfig.options.fullComparatorUrl = fullComparatorUrl;

  return clientConfig;
}

function setAbTesting(clientConfig, clientData) {
  const excludeFromAbTests = clientData.excludeFromAbTests;

  if (excludeFromAbTests) {
    clientConfig.options.abTesting = false;
  }

  return clientConfig;
}

function replacePhoneVariantIds(clientConfig, phoneVariantIds) {
  if (phoneVariantIds.length > 0) {
    clientConfig.limits.phoneVariantId = phoneVariantIds;
  }

  delete clientConfig.limits.phoneVariantEan;

  return clientConfig;
}

function replaceMaxPrice(clientConfig, phoneCategory) {
  if (phoneCategory) {
    const currentOfferType = offerType(clientConfig);
    if (currentOfferType !== OFFER_TYPES.phoneOffer) {
      clientConfig.widgetOptions[currentOfferType].initialFilter.maxPrice =
        phoneCategory.maxPrice;
    }
  }

  return clientConfig;
}

function mergeLimitsWithInitialFilters(clientConfig) {
  clientConfig.widgetOptions.phoneOffer.initialFilter = mergeLimitsWithFilter(
    clientConfig.limits,
    clientConfig.widgetOptions.phoneOffer.initialFilter,
    false,
    true
  );
  clientConfig.widgetOptions.proposition.initialFilter = mergeLimitsWithFilter(
    clientConfig.limits,
    clientConfig.widgetOptions.proposition.initialFilter,
    false,
    true
  );
  clientConfig.widgetOptions.simOnly.initialFilter = mergeLimitsWithFilter(
    clientConfig.limits,
    clientConfig.widgetOptions.simOnly.initialFilter,
    false,
    true
  );

  return clientConfig;
}

function setInIframe(clientConfig) {
  if (loadedInIframe()) {
    clientConfig.options.inIframe = true;
  }

  return clientConfig;
}

function setInGoogleAmp(clientConfig) {
  if (loadedInIframe()) {
    // Google AMP ads #amp=1 to the URL
    clientConfig.options.inGoogleAmp =
      window.location.href.indexOf("#amp=1") >= 0;
  }

  return clientConfig;
}

function setLanguage(clientConfig) {
  if (clientConfig.options.language == null) {
    clientConfig.options.language = portalConfig(
      clientConfig.portal
    ).defaultLanguage;
  }

  return clientConfig;
}

function loadedInIframe() {
  return window.location.href.indexOf("/pc-frontend-iframe.html") >= 0;
}

function updateI18nWithValues(i18n, language, namespace, key, value) {
  if ((typeof value === "string" || value instanceof String) && key != null) {
    i18n.addResource(language, namespace, key, value);
  } else if (value !== null && typeof value === "object") {
    for (const subKey of Object.keys(value)) {
      const newKey = key == null ? subKey : `${key}.${subKey}`;
      updateI18nWithValues(i18n, language, namespace, newKey, value[subKey]);
    }
  }
}

async function clientConfigIds() {
  await waitForClientConfigs();

  let clientConfig = window.tdsPcInpageConfig;

  let ids;
  if (typeof clientConfig !== "object") {
    ids = [];
  } else {
    ids = Object.keys(clientConfig);
  }

  return ids;
}

async function getInitialPortalData(clientConfig) {
  return await waitForInitialPortalData(clientConfig);
}

async function getCustomTheme(clientConfig, clientData) {
  await waitForCustomTheme(clientConfig, clientData);
}

const readClientConfig = (id) => {
  return window.tdsPcInpageConfig[id];
};

const clientConfig = (id) => {
  let config = readClientConfig(id);

  if (!config) {
    return defaultConfig();
  } else {
    if (config.urlQueryString) {
      const elementId = config.elementId;
      config = urlQueryStringToConfig(config.urlQueryString);
      config["elementId"] = elementId;
    }

    let mergedConfig = deepMerge(defaultConfig(config), config);
    mergedConfig.id = id;
    mergedConfig.limits = convertValues(mergedConfig.limits, LIMIT_FIELDS);
    mergedConfig.options = convertValues(mergedConfig.options, OPTION_FIELDS);
    mergedConfig.widgetOptions.phoneOffer = convertValues(
      mergedConfig.widgetOptions.phoneOffer,
      WIDGET_OPTION_FIELDS
    );
    mergedConfig.widgetOptions.proposition = convertValues(
      mergedConfig.widgetOptions.proposition,
      WIDGET_OPTION_FIELDS
    );
    mergedConfig.widgetOptions.simOnly = convertValues(
      mergedConfig.widgetOptions.simOnly,
      WIDGET_OPTION_FIELDS
    );
    mergedConfig.widgetOptions.phoneOffer.initialFilter = convertValues(
      mergedConfig.widgetOptions.phoneOffer.initialFilter,
      FILTER_FIELDS
    );
    mergedConfig.widgetOptions.proposition.initialFilter = convertValues(
      mergedConfig.widgetOptions.proposition.initialFilter,
      FILTER_FIELDS
    );
    mergedConfig.widgetOptions.simOnly.initialFilter = convertValues(
      mergedConfig.widgetOptions.simOnly.initialFilter,
      FILTER_FIELDS
    );

    return mergedConfig;
  }
};

async function domElement(clientConfig) {
  await waitForDomElement(clientConfig);

  return clientConfig.elementId;
}

const themeVariableName = (clientConfig) => {
  if (clientConfig.options.themeIdentifier) {
    return `tdsPcInpageTheme-${clientConfig.options.themeIdentifier}`;
  } else {
    return clientConfig.options.themeVariable;
  }
};

const updateClientConfig = (clientConfig, initialPortalData) => {
  let newClientConfig = { ...clientConfig };

  newClientConfig = addFullComparatorUrl(
    newClientConfig,
    initialPortalData.clientData
  );
  newClientConfig = setAbTesting(newClientConfig, initialPortalData.clientData);
  newClientConfig = replacePhoneVariantIds(
    newClientConfig,
    initialPortalData.phoneVariantIds
  );
  newClientConfig = replaceMaxPrice(
    newClientConfig,
    initialPortalData.phoneCategory
  );
  newClientConfig = mergeLimitsWithInitialFilters(newClientConfig);
  newClientConfig = setInGoogleAmp(newClientConfig);
  newClientConfig = setInIframe(newClientConfig);
  newClientConfig = setLanguage(newClientConfig);

  return newClientConfig;
};

const updateI18n = (i18n, clientConfig) => {
  if (Object.keys(clientConfig.options.languageUpdates).length >= 0) {
    const updates = clientConfig.options.languageUpdates;
    const language = clientConfig.options.language;

    for (const key of Object.keys(updates)) {
      // each key is a namespace, after that it are keys
      const namespace = key;
      updateI18nWithValues(i18n, language, namespace, null, updates[key]);
    }
  }
};

const checkClientConfig = (clientConfig) => {
  const requiredValues = ["elementId", "portal", "token"];

  for (const requiredValue of requiredValues) {
    if (!clientConfig[requiredValue]) {
      throw new Error(`TDS - clientConfig is missing ${requiredValue}`);
    }
  }

  if (clientConfig.portal && !PORTAL_IDS[clientConfig.portal]) {
    throw new Error(
      `TDS - clientConfig has invalid value for portal: ${clientConfig.portal}`
    );
  }

  if (clientConfig.widget && !WIDGETS[clientConfig.widget]) {
    throw new Error(
      `TDS - clientConfig has invalid value for widget: ${clientConfig.widget}`
    );
  }

  return true;
};

const showMultipleOfferTypes = (clientConfig) => {
  let offerTypeCount = 0;
  if (clientConfig.widgetOptions[OFFER_TYPES.phoneOffer].show) {
    offerTypeCount += 1;
  }

  if (clientConfig.widgetOptions[OFFER_TYPES.proposition].show) {
    offerTypeCount += 1;
  }

  if (clientConfig.widgetOptions[OFFER_TYPES.simOnly].show) {
    offerTypeCount += 1;
  }

  return offerTypeCount > 1;
};

export {
  checkClientConfig,
  clientConfig,
  clientConfigIds,
  domElement,
  getCustomTheme,
  getInitialPortalData,
  readClientConfig,
  showMultipleOfferTypes,
  themeVariableName,
  updateClientConfig,
  updateI18n
};
